/**
 * Framework-agnostic SDK consent dialog. Rendered by the in-Papan previewer and
 * share-view (features/collab/ui/SdkConsentDialog.tsx wraps it in a thin React
 * shell) for BOTH the consent prompt and the "Manage permissions" view.
 *
 * NOT the only consent renderer in the product: the ppn.gg App-Mode page
 * (apps/web/public/__accounts/consent.html) has its own and does not call
 * `mountConsentDialog`. This docblock used to claim it did, which would tell the
 * next reader a change here covers a surface it does not touch. Formerly:
 *   1. the in-Papan previewer (features/collab/ui/SdkConsentDialog.tsx wraps
 *      `mountConsentDialog` in a thin React shell), and
 *   2. the ppn.gg App-Mode consent popup (apps/api serves a page that loads
 *      this bundle; the router BFF proxies it same-origin).
 *
 * Vanilla DOM + a scoped <style> block (NO Tailwind) so it renders identically
 * on a bare popup window and inside the SPA. No framework, no providers — the
 * dialog only ever needed local state + a fetch.
 *
 * SECURITY: the display strings (`appName`, `appOwnerName`, `required`,
 * `optional`) must come from the SERVER, never from the embedding app — see
 * R21 #140 (brand-spoof). On ppn.gg the popup page re-fetches them from
 * `/api/v1/sdk/consent/requirement`; it never trusts values the app passes in.
 */

export type ConsentChoice = 'allow_all' | 'required_only' | 'decline'

export interface ConsentDecision {
  decision: ConsentChoice
  grantedFields: string[]
}

export interface ConsentRequest {
  appFolderId: string
  appName: string
  /** Display name of the tenant hosting the app folder. Disclosed so the
   *  user knows who receives their data (load-bearing for ppn.gg, where the
   *  user has no implicit tenant context). May be null if the server lookup
   *  failed — the dialog renders without the "by …" line in that case. */
  appOwnerName: string | null
  required: string[]
  optional: string[]
  /** Current grants, when re-opening the dialog to MANAGE consent (from the
   *  bar's Permissions). Optional toggles start reflecting these instead of
   *  defaulting all-on; absent (first-prompt flow) → optional default on.
   *  Its PRESENCE is what puts the dialog in manage mode. */
  granted?: string[]
  /** Fields the user has REFUSED. Rendered like any other row, switched off.
   *
   *  Omitting it was a real hole: `declined` is a decision the record holds,
   *  and a field that was declined and has since been dropped from the
   *  manifest appeared in neither list — so the user could see no trace of it
   *  and had no way to change their mind. The requirement endpoint has always
   *  returned `alreadyDeclined`; the host simply dropped it. */
  declined?: string[]
  /** Fields being asked about for the FIRST time — the manifest declared them
   *  after the user had already decided about the others.
   *
   *  Two jobs, and both were being done wrong by its absence. It marks the
   *  dialog as a PROMPT rather than a management view (the footer follows),
   *  and it is the only set that may default ON: a delta prompt that defaults
   *  every optional field on silently re-grants one the user had DECLINED, the
   *  moment they press Allow. The host has always passed this; the dialog
   *  ignored it. */
  newFields?: string[]
}

/** Row groups, in render order. `record` is the residue: a decision the user
 *  made about a field the manifest has since stopped declaring. */
export type ConsentRowKind = 'req' | 'opt' | 'record'

/** 16px monochrome glyphs, inline so the dialog stays a single self-contained
 *  bundle (it renders on a bare popup with no asset pipeline). Purely
 *  decorative — every row is already labelled — so they are aria-hidden. */
const FIELD_ICON: Record<string, string> = {
  name: '<circle cx="8" cy="5.2" r="2.8"/><path d="M2.5 14c0-3 2.5-4.6 5.5-4.6s5.5 1.6 5.5 4.6"/>',
  email: '<rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.4"/><path d="M2.4 4.6 8 8.8l5.6-4.2"/>',
  org: '<rect x="2.2" y="2.6" width="7" height="11" rx="1"/><path d="M9.2 6.4h4.6v7.2H9.2"/><path d="M4.2 5.2h3M4.2 7.6h3M4.2 10h3"/>',
  teams: '<circle cx="5.6" cy="5.6" r="2.3"/><circle cx="11" cy="6.4" r="1.8"/><path d="M1.6 13.4c0-2.4 1.8-3.8 4-3.8s4 1.4 4 3.8"/><path d="M10.4 9.8c2 .1 3.5 1.3 3.5 3.6"/>',
  role: '<path d="M8 1.8 9.8 5.5l4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4L2.2 6.1l4-.6z"/>',
  team_role: '<path d="M8 1.8 9.8 5.5l4 .6-2.9 2.8.7 4-3.6-1.9-3.6 1.9.7-4L2.2 6.1l4-.6z"/>',
}

function iconFor(doc: Document, field: string): SVGElement {
  const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('class', 'pc-icon')
  svg.setAttribute('viewBox', '0 0 16 16')
  svg.setAttribute('fill', 'none')
  svg.setAttribute('stroke', 'currentColor')
  svg.setAttribute('stroke-width', '1.3')
  svg.setAttribute('stroke-linecap', 'round')
  svg.setAttribute('stroke-linejoin', 'round')
  svg.setAttribute('aria-hidden', 'true')
  // A field with no glyph gets an empty frame rather than a fallback shape —
  // a wrong icon is worse than none, and the label already names the field.
  svg.innerHTML = Object.prototype.hasOwnProperty.call(FIELD_ICON, field) ? FIELD_ICON[field] : ''
  return svg
}

// Labels keyed by the CANONICAL grantable consent keys. No `email_verified` — it's not
// an independently-grantable field (it rides `email`); offering it as a toggle would
// invite a manifest to declare it and surface a phantom key in `actor.consented`.
export const FIELD_LABELS: Record<string, string> = {
  email: 'Your email',
  name: 'Your name',
  org: 'Your organization (name + id)',
  role: 'Your role in the organization',
  teams: 'Which teams you belong to',
  team_role: 'Your admin status within each team',
}

export function labelFor(field: string): string {
  // hasOwnProperty guard: a field literally named `constructor`/`toString`/etc. would
  // otherwise resolve to the inherited Object.prototype member instead of falling back
  // to the field name. (The manifest charset cap `^[a-z0-9_]+$` permits those names.)
  return Object.prototype.hasOwnProperty.call(FIELD_LABELS, field) ? FIELD_LABELS[field] : field
}

/** Map a decision to the granted/declined field sets. Pure — shared by the
 *  renderer and unit-testable on its own. */
export function computeConsentSets(
  decision: ConsentChoice,
  required: string[],
  optional: string[],
): { granted: string[]; declined: string[] } {
  if (decision === 'allow_all') return { granted: [...required, ...optional], declined: [] }
  if (decision === 'required_only') return { granted: [...required], declined: [...optional] }
  return { granted: [], declined: [...required, ...optional] }
}

export interface RecordConsentResult {
  ok: boolean
  grantedFields?: string[]
  error?: string
}

/** POST the decision to `/api/v1/sdk/consent` (default). Same-origin with
 *  `credentials: 'include'` works on papan.ai (Better Auth cookie) and the
 *  workspace previewer. `baseUrl` defaults to same-origin; override only for tests.
 *
 *  `endpoint` overrides the path: the SHARE-VIEW writes consent FIRST-PARTY via
 *  `/api/v1/accounts/ppn-consent` instead — the default `/sdk/consent` rejects any
 *  request carrying a share session (`consent_must_be_first_party`, the self-grant
 *  guard). `extraBody` merges into the JSON body (the share-view passes the acting
 *  `{ tenantId, email }` + the `appToken` so the first-party endpoint keys the grant
 *  on the picked org identity and can resolve the link grant for its canRead gate). */
export async function recordConsent(
  input: { appFolderId: string; decision: ConsentChoice; granted: string[]; declined: string[] },
  opts: {
    fetchImpl?: typeof fetch
    baseUrl?: string
    headers?: Record<string, string>
    endpoint?: string
    extraBody?: Record<string, unknown>
    /** Give up after this long. Default 30s — see `recordConsent`. */
    timeoutMs?: number
  } = {},
): Promise<RecordConsentResult> {
  const fetchImpl = opts.fetchImpl ?? fetch
  const baseUrl = opts.baseUrl ?? ''
  // A STALLED write must not be forever. The dialog disables every button while
  // this is in flight — footer, backdrop and Escape — so a fetch that neither
  // resolves nor rejects (a proxy hang, a network transition mid-request) left
  // the user inside a full-screen `position:fixed; inset:0; z-index:9999`
  // overlay with no exit at all, and nothing beneath it clickable. Only a tab
  // reload got them out, losing whatever else the deck held.
  //
  // Aborting is the right shape rather than re-enabling Escape mid-flight: the
  // buttons are disabled precisely so a dismiss cannot resolve `null` while a
  // write is still landing, and that reasoning does not stop being true because
  // the request is slow. So end the request instead — the error path already
  // re-enables everything and shows a message, which is a state the user can
  // act on.
  const timeoutMs = opts.timeoutMs ?? 30_000
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer: ReturnType<typeof setTimeout> | null = null
  // RACED, not merely signalled. Aborting tells `fetch` to give up, and a real
  // one then rejects — but that makes settlement the fetch implementation's
  // promise to keep, and the whole point here is that the dialog cannot be left
  // waiting. The race is the guarantee; the abort is the courtesy that stops the
  // request actually going out.
  const timedOut = Symbol('consent-timeout')
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => { ctl?.abort(); reject(timedOut) }, timeoutMs)
  })
  let res: Response
  try {
    res = await Promise.race([deadline, fetchImpl(`${baseUrl}${opts.endpoint ?? '/api/v1/sdk/consent'}`, {
      method: 'POST',
      credentials: 'include',
      ...(ctl ? { signal: ctl.signal } : {}),
      // R15 — the host MUST forward the active-tenant headers (x-tenant-id / x-org-email).
      // Without them, resolveActiveTenantIdentity defaults to the user's PERSONAL tenant,
      // so an org-workspace grant keys on personal while the requirement read + scripts.run
      // gate key on the active org tenant → the grant is invisible ("Allow does nothing").
      // The old in-frame embed POST sent these; the in-host path must too.
      headers: { 'Content-Type': 'application/json', ...opts.headers },
      body: JSON.stringify({
        appFolderId: input.appFolderId,
        decision: input.decision,
        grantedFields: input.granted,
        declinedFields: input.declined,
        ...opts.extraBody,
      }),
    })])
    // THE BODY IS RACED TOO, and the timer is cleared only after it.
    //
    // `fetch` resolves as soon as the response HEADERS arrive; the body is read
    // afterwards. Clearing the timer at the end of the fetch therefore disarmed
    // the deadline at exactly the moment it still had work to do — a response
    // that delivers headers and then stalls its body left `res.json()` pending
    // forever, `submitting` true, and every exit disabled: the same modal with
    // no way out, a few hundred milliseconds later than the case that was fixed.
    if (!res.ok) {
      const errBody = await Promise.race([deadline, res.json().catch(() => null)])
      return { ok: false, error: (errBody && (errBody as { error?: string }).error) || `consent recording failed (${res.status})` }
    }
    const body = (await Promise.race([deadline, res.json().catch(() => ({}))])) as { grantedFields?: unknown }
    return {
      ok: true,
      grantedFields: Array.isArray(body.grantedFields) ? (body.grantedFields as string[]) : input.granted,
    }
  } catch (err) {
    // Our own timeout, not the server's answer — say so in words the user can
    // act on rather than surfacing `AbortError`.
    //
    // The message is deliberately about REACHABILITY, not about the server's
    // state, because we do not know it: a write that commits at 31s is reported
    // here as "not saved" while the grant is live. That is the permissive
    // direction for Allow — the user believes they did not grant while the app
    // can read their identity — and it is why the dialog stays OPEN on this
    // path rather than resolving. Retrying is idempotent (the row is an upsert,
    // last write wins with the same payload), so the recovery is the obvious
    // one. Nothing re-reads the record here; a user who gives up instead of
    // retrying keeps a false belief about their own consent.
    const aborted = err === timedOut || !!ctl?.signal.aborted
    return {
      ok: false,
      error: aborted
        ? "Couldn't reach the server — your choice may not have been saved. Please try again."
        : err instanceof Error ? err.message : String(err),
    }
  } finally {
    if (timer !== null) clearTimeout(timer)
  }
}

export interface MountConsentOptions {
  /** Called once with the user's decision (or null if dismissed without a
   *  choice — backdrop click / Escape). The dialog records the decision via
   *  `recordConsent` BEFORE resolving on a real choice; a dismiss does not
   *  POST anything. */
  onResolve: (decision: ConsentDecision | null) => void
  fetchImpl?: typeof fetch
  baseUrl?: string
  /** Extra headers forwarded to the consent POST. The previewer host passes the active
   *  tenant (x-tenant-id / x-org-email via getDefaultHeaders) so an org-workspace grant
   *  keys on the ACTIVE tenant, matching the requirement read + scripts.run gate (R15). */
  headers?: Record<string, string>
  /** Override the consent POST path. The share-view sets
   *  `/api/v1/accounts/ppn-consent` (first-party write; the default `/sdk/consent`
   *  rejects share-session principals). Defaults to `/api/v1/sdk/consent`. */
  endpoint?: string
  /** Extra fields merged into the consent POST body — the share-view passes the
   *  acting `{ tenantId, email }` + `appToken` for the first-party endpoint. */
  extraBody?: Record<string, unknown>
  /** Allow dismiss via backdrop/Escape. Default true (previewer overlay).
   *  The ppn.gg popup sets false — closing the window is the dismiss. */
  dismissible?: boolean
  /** How long to wait for the consent write before giving up. Every exit is
   *  disabled while it is in flight, so this is what stops a stalled request
   *  becoming a modal with no way out — see `recordConsent`. */
  timeoutMs?: number
}

/** Every dismissible dialog currently on screen, oldest first.
 *
 *  Escape must be answered by the TOP one, and DOM listeners on a single node
 *  fire in registration order — which is mount order, i.e. bottom-first. So a
 *  handler that acted as soon as it ran dismissed the dialog UNDERNEATH the one
 *  the user was looking at, while the visible one did not move. Reachable: two
 *  viewers for two different app folders are both mounted (a backgrounded deck
 *  is hidden, not unmounted), the fence is per-folder so both prompt, and both
 *  portal to `document.body` where the deck's `opacity-0` cannot reach them.
 *
 *  Each dialog checks it is last before acting, so the bottom one returns
 *  WITHOUT stopping the event and the top one gets it. */
const openDialogs: object[] = []

const STYLE_ID = 'papan-consent-style'

const STYLE_CSS = `
/* The glass dialog (docs/DESIGN_DOC.md §10.5, the .ws-glass-modal material): uniform translucency
   with a slight alpha fade, a 1px glass hairline, backdrop blur, a soft drop shadow and a faint inner
   top-light. No other borders — rows and groups separate by FILL and space (design rule 7). Dark is
   the default; the host puts its theme class (theme-dim / theme-light) on the dialog's container. */
.pc-overlay{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.45);backdrop-filter:blur(2px);animation:pc-fade .2s ease-out;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --pc-glass:linear-gradient(180deg,rgba(22,22,28,.58),rgba(22,22,28,.46));--pc-edge:rgba(255,255,255,.12);--pc-shadow:0 24px 64px rgba(0,0,0,.6),inset -1px 1px 1px rgba(255,255,255,.25);
  --pc-text:#f4f4f5;--pc-text-2:rgba(255,255,255,.72);--pc-text-3:rgba(255,255,255,.5);--pc-row:rgba(255,255,255,.06);--pc-row-hover:rgba(255,255,255,.1);--pc-tshadow:0 1px 3px rgba(0,0,0,.4);
  --pc-switch-off:rgba(255,255,255,.18);--pc-on:#10b981;--pc-accent:#2563eb;--pc-accent-hover:#3b82f6;--pc-danger:#f87171}
.theme-dim .pc-overlay{--pc-glass:linear-gradient(180deg,rgba(32,32,58,.63),rgba(32,32,58,.5));--pc-edge:rgba(238,239,255,.14)}
.theme-light .pc-overlay{background:rgba(30,32,48,.22);
  --pc-glass:linear-gradient(180deg,rgba(255,255,255,.85),rgba(255,255,255,.64));--pc-edge:rgba(255,255,255,.85);--pc-shadow:0 24px 64px rgba(71,85,105,.25),inset -1px -1px 0 rgba(255,255,255,.5);
  --pc-text:#18181b;--pc-text-2:#52525b;--pc-text-3:#71717a;--pc-row:rgba(24,24,27,.045);--pc-row-hover:rgba(24,24,27,.08);--pc-tshadow:0 1px 2px rgba(255,255,255,.6);
  --pc-switch-off:rgba(24,24,27,.18);--pc-danger:#dc2626}
@keyframes pc-fade{from{opacity:0}to{opacity:1}}
@keyframes pc-in{from{opacity:0;transform:scale(.96) translateY(8px)}to{opacity:1;transform:none}}
.pc-panel{width:440px;max-width:100%;border-radius:16px;background:var(--pc-glass);border:1px solid var(--pc-edge);box-shadow:var(--pc-shadow);backdrop-filter:blur(28px) saturate(1.7);-webkit-backdrop-filter:blur(28px) saturate(1.7);color:var(--pc-text);overflow:hidden;animation:pc-in .24s cubic-bezier(0.3,0,0,1);text-shadow:var(--pc-tshadow)}
.pc-head{padding:20px 20px 4px;font-size:15px}
.pc-head-title{color:var(--pc-text-2)}
.pc-head-title strong{color:var(--pc-text);font-weight:600}
.pc-head-owner{margin-top:3px;font-size:12px;color:var(--pc-text-3)}
.pc-head-owner span{color:var(--pc-text-2)}
.pc-body{padding:12px 16px 4px;font-size:14px;display:flex;flex-direction:column;gap:14px}
.pc-empty{color:var(--pc-text-2);padding:0 4px}
/* Controls at the END of the row, so every label starts at the same left edge. Each row is a soft
   fill on the glass (state by fill, never an outline). */
.pc-field{display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:12px;background:var(--pc-row);transition:background .15s}
.pc-field:hover{background:var(--pc-row-hover)}
.pc-icon{flex-shrink:0;width:16px;height:16px;color:var(--pc-text-3);display:block}
.pc-field-label{font-weight:500;flex:1;min-width:0}
.pc-toggle{position:relative;width:36px;height:22px;border-radius:11px;border:none;padding:0;flex-shrink:0;cursor:pointer;background:var(--pc-switch-off);transition:background .15s}
.pc-toggle.on{background:var(--pc-on)}
.pc-toggle-req.on{background:color-mix(in srgb,var(--pc-on) 60%,transparent)}
.pc-toggle:disabled{cursor:default}
.pc-knob{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.35);transition:left .15s}
.pc-toggle.on .pc-knob{left:16px}
/* Forget — distinct from switching off. Off RECORDS a refusal; this drops the
   decision entirely, so the app asks again if it ever needs the field. */
.pc-forget{flex-shrink:0;width:24px;height:24px;border:none;border-radius:8px;background:transparent;color:var(--pc-text-3);cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0}
.pc-forget:hover:not(:disabled){background:rgba(239,68,68,.14);color:var(--pc-danger)}
.pc-forget:disabled{opacity:.4;cursor:default}
.pc-field.is-forgotten .pc-field-label{text-decoration:line-through;color:var(--pc-text-3)}
.pc-field.is-forgotten .pc-icon{opacity:.4}
.pc-field.is-forgotten .pc-toggle{opacity:.35;pointer-events:none}
.pc-forget.is-undo{color:var(--pc-text-2)}
/* The state in WORDS beside the switch ("Allowed" is the word the record uses). Muted, un-boxed. */
.pc-state{font-size:11px;color:var(--pc-text-3);flex-shrink:0;min-width:56px;text-align:right}
.pc-field.is-forgotten .pc-state{color:var(--pc-text-3)}
/* Amber, not green, for a field the app has stopped asking for: the switch still governs, but a
   REQUEST that is not being made — so it should not look identical to a live grant. */
.pc-toggle-record.on{background:#b45309}
/* Group label carries the category (Required / Optional / no longer asked for). Groups separate by
   space, not a rule. */
.pc-group{display:flex;flex-direction:column;gap:6px}
.pc-group-label{font-size:10.5px;font-weight:600;text-transform:uppercase;letter-spacing:.07em;color:var(--pc-text-3);padding:0 4px 2px}
.pc-notes{font-size:12px;line-height:1.45;color:var(--pc-text-3);padding:0 4px;display:flex;flex-direction:column;gap:4px}
.pc-warn{color:#f59e0b}
.pc-error{font-size:12px;color:var(--pc-danger);background:rgba(239,68,68,.12);border-radius:10px;padding:6px 10px}
.pc-foot{padding:16px;display:flex;align-items:center;justify-content:flex-end;gap:8px;font-size:14px}
.pc-btn{height:36px;padding:0 16px;border-radius:10px;border:none;cursor:pointer;background:var(--pc-row);color:var(--pc-text);font:inherit;font-size:14px;font-weight:500;text-shadow:none;transition:background .15s}
.pc-btn:hover:not(:disabled){background:var(--pc-row-hover)}
.pc-btn:disabled{opacity:.5;cursor:default}
.pc-btn-decline{color:var(--pc-danger)}
.pc-btn-decline:hover:not(:disabled){background:rgba(239,68,68,.14)}
.pc-btn-revoke{margin-right:auto;background:transparent;color:var(--pc-text-2);padding:0 8px}
.pc-btn-revoke:hover:not(:disabled){background:rgba(239,68,68,.12);color:var(--pc-danger)}
.pc-btn-allow{background:var(--pc-accent);color:#fff;font-weight:600}
.pc-btn-allow:hover:not(:disabled){background:var(--pc-accent-hover)}
@media (prefers-reduced-motion:reduce){.pc-overlay,.pc-panel{animation:none}}
`

function ensureStyles(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = STYLE_CSS
  doc.head.appendChild(style)
}

function el(doc: Document, tag: string, className?: string, text?: string): HTMLElement {
  const node = doc.createElement(tag)
  if (className) node.className = className
  if (text != null) node.textContent = text
  return node
}

/**
 * Render the consent dialog into `container` and wire its buttons. Returns a
 * cleanup function that removes the dialog DOM (the React wrapper calls it on
 * unmount; the popup page lets the window close).
 */
export function mountConsentDialog(
  container: HTMLElement,
  request: ConsentRequest,
  options: MountConsentOptions,
): () => void {
  const doc = container.ownerDocument
  ensureStyles(doc)
  const dismissible = options.dismissible !== false

  let submitting = false
  let settled = false
  /** This dialog's identity in `openDialogs`. Function-scoped, because
   *  `cleanup` (declared below, called on unmount) has to remove the same
   *  token the keydown registration pushed. */
  const dialogToken = {}

  const overlay = el(doc, 'div', 'pc-overlay')
  const panel = el(doc, 'div', 'pc-panel')
  overlay.appendChild(panel)

  // Header
  const head = el(doc, 'div', 'pc-head')
  const headTitle = el(doc, 'div', 'pc-head-title')
  const strong = el(doc, 'strong')
  strong.textContent = request.appName
  headTitle.appendChild(strong)
  // "wants to know" is only true while it is ASKING. With nothing declared the
  // dialog is a record of what the app already has, so the app name stands
  // alone and the body says what the situation is.
  if (request.required.length > 0 || request.optional.length > 0) {
    headTitle.appendChild(doc.createTextNode(' wants to know:'))
  }
  head.appendChild(headTitle)
  if (request.appOwnerName) {
    const owner = el(doc, 'div', 'pc-head-owner')
    owner.appendChild(doc.createTextNode('by '))
    owner.appendChild(el(doc, 'span', undefined, request.appOwnerName))
    head.appendChild(owner)
  }
  panel.appendChild(head)

  // Body
  const body = el(doc, 'div', 'pc-body')

  /** Every field the record holds a decision about, plus everything the
   *  manifest asks for — grouped, because the three cases differ in what the
   *  user can DO, not just in wording.
   *
   *  `record` is the residue: a decision about a field the manifest has since
   *  stopped declaring. It stays editable. The record outlives the manifest,
   *  so the user keeps the right to change their mind about it — that is the
   *  whole premise of #525, and a read-only row here would have been the same
   *  dead-end one step in. (`POST /consent` widens its clamp to fields already
   *  on record, or a switch here could not persist and would be a control that
   *  does nothing.) */
  // Whether the caller told us what is ON RECORD, and whether this is a
  // management view. They are NOT the same question: a delta prompt knows the
  // record AND is a prompt, and conflating them is what let it re-grant a
  // refusal.
  const seeded = Array.isArray(request.granted)
  const newFields = Array.isArray(request.newFields) ? request.newFields : null
  const manage = seeded && newFields === null
  const grantedIn = new Set(request.granted ?? [])
  const declinedIn = new Set(request.declined ?? [])
  const declared = new Set([...request.required, ...request.optional])
  const recordOnly = [...new Set([...grantedIn, ...declinedIn])].filter((f) => !declared.has(f))
  /** The residue AS RENDERED. Only Manage shows it (see `addGroup` below), so
   *  this is what the body copy and the footer must both be keyed on.
   *
   *  They were keyed on `recordOnly` and `manage` respectively — two different
   *  predicates for one question — so a PROMPT whose manifest asked for nothing
   *  while the record held something printed "Here's what it has on record:"
   *  above zero rows, under a Decline / Allow footer warning about blocking an
   *  app that was asking for nothing. Describing controls that are not there is
   *  the defect this dialog exists to remove; one predicate cannot disagree
   *  with itself. */
  const shownRecordOnly = manage ? recordOnly : []
  const nothingRequested = request.required.length === 0 && request.optional.length === 0

  /** Whether the caller actually TOLD us what is on record.
   *
   *  `granted` absent is not `granted: []`. The requirement endpoint returns
   *  early — without reading the record — when `manifest.json` is missing
   *  entirely, so the answer there is unknown rather than none. Claiming "no
   *  access" on an unknown would be the same false confidence this change
   *  exists to remove. */
  const recordKnown = seeded
  if (nothingRequested && shownRecordOnly.length === 0) {
    body.appendChild(el(doc, 'div', 'pc-empty', recordKnown
      ? 'This app has no access to your identity.'
      : `${request.appName || 'It'} doesn't ask to know anything about you.`))
  } else if (nothingRequested) {
    body.appendChild(el(doc, 'div', 'pc-empty', "This app isn't asking for anything right now. Here's what it has on record:"))
  }

  // ── State the footer reads ──────────────────────────────────────────
  //
  // `on`   — which fields end up granted.
  // `gone` — which fields are dropped from the record entirely. Distinct from
  //          switching off: off RECORDS a refusal, this forgets the decision,
  //          so the app asks again if it ever needs the field.
  //
  // FIRST prompt (nothing on record): optional default ON — switch off what
  // you'd keep private. Otherwise every row starts at its CURRENT state, so
  // the dialog reflects what is actually shared, and only a genuinely NEW
  // field defaults on. Defaulting a declined field on and letting Allow
  // rewrite it is a refusal reversed by a control the user never touched.
  const on = new Set<string>(
    seeded
      ? [...request.required, ...request.optional, ...recordOnly]
        .filter((f) => grantedIn.has(f) || (newFields !== null && newFields.includes(f)))
      : [...request.required, ...request.optional],
  )
  // Only a PROMPT forces required on — there it means "this is what Allow will
  // grant". While MANAGING, `on` is a claim about the RECORD, and the state
  // beside each switch prints it verbatim. Forcing it there said "Allowed" for
  // a required field the user had DECLINED (the app is 403-blocked on exactly
  // that refusal), and Save — the affirmative-looking "keep what is on screen"
  // — then made the lie true and unblocked the app. The switch stays locked
  // either way; locking it is a statement about choice, not about state.
  if (!manage) for (const f of request.required) on.add(f)
  const gone = new Set<string>()
  /** Fields the record says NOTHING about, and the user has not touched here.
   *
   *  A two-position switch has to represent three record states — granted,
   *  declined, and never-asked — and collapsing the third into "off" made Save
   *  write a refusal for a field nobody had ever been asked about. The proactive
   *  gate computes `newFields` as declared-minus-granted-minus-declined, so once
   *  that refusal lands the app can never prompt for it again: the user is opted
   *  out of something they never saw a question about.
   *
   *  So an untouched undecided field is left out of BOTH arrays and stays
   *  undecided. Touching its switch is what turns it into a decision. */
  const undecided = new Set<string>(
    seeded
      ? [...request.required, ...request.optional].filter((f) => !grantedIn.has(f) && !declinedIn.has(f))
      : [],
  )
  const touched = new Set<string>()

  const addRow = (parent: HTMLElement, field: string, kind: ConsentRowKind) => {
    const isReq = kind === 'req'
    const row = el(doc, 'div', 'pc-field')
    row.appendChild(iconFor(doc, field))
    row.appendChild(el(doc, 'div', 'pc-field-label', labelFor(field)))

    const state = el(doc, 'span', 'pc-state')
    /** THE row's only state writer. `gone` comes first because staging a row
     *  for Forget overrides what its switch says — Save drops the field from
     *  both arrays, so neither "Allowed" nor "Declined" is what will happen.
     *
     *  It reads `gone` rather than leaving that to the residue's own painter
     *  because there used to be TWO writers of this one node and they could
     *  disagree: the switch is not `disabled` while staged (only
     *  `pointer-events:none`, which stops a mouse and not the keyboard), so
     *  Space on a focused staged toggle called the state painter, which knew
     *  nothing about `gone` and overwrote "Will be forgotten" with "Declined"
     *  — a struck-through row, an undo button, and a word describing none of
     *  it. One writer cannot drift from itself. */
    const paintState = () => {
      state.textContent = gone.has(field)
        ? 'Will be forgotten'
        : on.has(field)
          ? 'Allowed'
          : (undecided.has(field) && !touched.has(field)) ? 'Not set' : 'Declined'
    }
    paintState()
    row.appendChild(state)

    const toggle = el(doc, 'button', `pc-toggle pc-toggle-${kind}${on.has(field) ? ' on' : ''}`) as HTMLButtonElement
    toggle.type = 'button'
    toggle.setAttribute('role', 'switch')
    toggle.setAttribute('aria-checked', on.has(field) ? 'true' : 'false')
    toggle.setAttribute('aria-label', labelFor(field))
    toggle.appendChild(el(doc, 'span', 'pc-knob'))
    if (isReq) {
      // Locked on, everywhere. A required field is not a choice while you use
      // the app, so a switch that moves would be describing a decision you
      // cannot make. Revoking it is an APP-level act, not a per-field one —
      // "Decline all" in the footer is where that lives.
      toggle.disabled = true
      // The tooltip must match the STATE, not the category — and the record
      // has THREE states, not two. Once managing stopped forcing required on,
      // a declined required field kept a tooltip reading "always shared";
      // deriving "declined" from `!on` then told a user who has decided
      // NOTHING that they had refused — the row beside it correctly reading
      // "Not set" — on the one dialog whose job is to state the record
      // accurately. Both wrong answers name a decision the user did not make.
      toggle.title = on.has(field)
        ? 'Required to use this app — always shared'
        : undecided.has(field)
          ? "Required, and you haven't decided yet — it won't run until you do. Reopen it to decide."
          : 'Required, and you declined it — it won\'t run for you. Reopen it to decide again.'
    } else {
      toggle.addEventListener('click', () => {
        const nowOn = toggle.classList.toggle('on')
        toggle.setAttribute('aria-checked', nowOn ? 'true' : 'false')
        if (nowOn) on.add(field)
        else on.delete(field)
        touched.add(field)   // an untouched undecided field stays undecided
        paintState()
      })
    }
    row.appendChild(toggle)

    // Forget belongs to the residue, and ONLY there.
    //
    // On a row the app still asks about, the switch already says everything:
    // off records a refusal and the app honours it. Forget would only mean
    // "ask me again next load", which is a second control for an outcome
    // almost nobody wants and reads as redundant beside the switch.
    //
    // For a field the app has STOPPED asking about, off and forgotten are
    // genuinely different: off keeps a standing refusal on the record, forget
    // removes the entry so a later request starts clean. That distinction is
    // the whole reason this control exists.
    if (kind === 'record') {
      const forget = el(doc, 'button', 'pc-forget') as HTMLButtonElement
      forget.type = 'button'
      const paint = () => {
        const staged = gone.has(field)
        // The word beside the switch moves too — through `paintState`, the
        // row's single state writer, which reads `gone` itself. Painting it
        // here as well was the second writer that could disagree with the
        // first (see `paintState`).
        paintState()
        // Genuinely disabled while staged, not merely `pointer-events:none`.
        // The CSS stops a mouse and not the keyboard, so a focused staged
        // toggle still took Space — and assistive tech announced an operable
        // switch that a mouse user could not operate. Save drops the field
        // either way, so there is nothing for the switch to say here.
        toggle.disabled = staged
        row.classList.toggle('is-forgotten', staged)
        forget.classList.toggle('is-undo', staged)
        forget.title = staged ? 'Keep this decision' : "Forget this decision — you'll be asked again"
        forget.setAttribute('aria-label', `${staged ? 'Keep' : 'Forget'} ${labelFor(field)}`)
        forget.textContent = staged ? '↺' : '✕'
      }
      forget.addEventListener('click', () => {
        if (gone.has(field)) gone.delete(field)
        else gone.add(field)
        paint()
      })
      paint()
      row.appendChild(forget)
    }
    parent.appendChild(row)
  }

  const addGroup = (label: string, fields: string[], kind: ConsentRowKind) => {
    if (fields.length === 0) return
    const group = el(doc, 'div', 'pc-group')
    group.appendChild(el(doc, 'div', 'pc-group-label', label))
    fields.forEach((f) => addRow(group, f, kind))
    body.appendChild(group)
  }
  addGroup('Required', request.required, 'req')
  addGroup('Optional', request.optional, 'opt')
  // MANAGE only. A prompt is about what the app is ASKING for; the residue is
  // by definition what it has stopped asking for, so it is not part of that
  // decision — and Decline / Allow read no `gone`, so every ✕ rendered here on
  // a prompt was a control that did nothing. The residue stays visible and
  // editable where it belongs, in Manage.
  //
  // NOT rendering it is not the same as not WRITING it: Decline / Allow carry
  // `residueGranted` / `residueDeclined` through unchanged, because the POST
  // replaces both columns and silence would delete them.
  addGroup('No longer requested', shownRecordOnly, 'record')

  // Keyed on what is ON SCREEN. These branched on `manage` while the footer
  // branched on `nothingAtAll`, so the Close-only dialog inherited whichever
  // block `manage` happened to pick — including an amber warning that "Clicking
  // Decline blocks this app", under a dialog whose only button is Close.
  // Describing controls that are not there is the defect this issue is about.
  const nothingAtAll = nothingRequested && shownRecordOnly.length === 0
  const notes = el(doc, 'div', 'pc-notes')
  if (nothingAtAll) {
    // No rows, no decisions — the body line already says so.
  } else if (manage) {
    // Same three states the row labels use, and for the same reason: deriving
    // "declined" from `!on` told a user who had decided NOTHING about a
    // required field that they had refused it, two inches under a row reading
    // "Not set". `undecided` is only ever populated in manage mode, and a
    // required row has no click handler, so `touched` cannot apply here.
    const declinedRequired = request.required.filter((f) => !on.has(f) && !undecided.has(f))
    const undecidedRequired = request.required.filter((f) => undecided.has(f))
    notes.appendChild(el(doc, 'div', undefined, declinedRequired.length > 0
      ? `You declined a required field, so ${request.appName || 'it'} won't run for you. Reopen it to decide again.`
      : undecidedRequired.length > 0
        ? `You haven't decided about a required field yet, so ${request.appName || 'it'} won't run for you. Reopen it to decide.`
        // Only promise a locked switch when one is on screen. An app declaring
        // ONLY optional fields renders no Required group, and this sentence
        // then described a control that is not there — the defect this dialog
        // exists to remove, one combination further out.
        : request.required.length > 0
          ? `Switching a field off stops sharing it. Required fields are always shared while you use ${request.appName || 'it'}. Decline all refuses every field — it won't run for you until you allow its required fields.`
          : 'Switching a field off stops sharing it. Decline all refuses every field.'))
    if (shownRecordOnly.length > 0) {
      notes.appendChild(el(doc, 'div', undefined, `${request.appName || 'It'} no longer asks for the fields under “No longer requested”. Your decision stays on record until you change it — Forget (✕) removes the entry, so you start clean if it ever asks again.`))
    }
  } else {
    if (request.optional.length > 0) {
      notes.appendChild(el(doc, 'div', undefined, `${request.appName || 'It'} stamps your identity on writes you make. Switch off any optional field to keep it private — it sees it as empty.`))
    }
    const declineNote = el(doc, 'div')
    declineNote.appendChild(el(doc, 'span', 'pc-warn', 'Clicking Decline'))
    declineNote.appendChild(doc.createTextNode(` blocks ${request.appName || 'it'} from running for you — nothing here runs until you allow it. Reopen it to decide again.`))
    notes.appendChild(declineNote)
  }
  if (notes.childNodes.length > 0) body.appendChild(notes)
  const errorBox = el(doc, 'div', 'pc-error')
  errorBox.style.display = 'none'
  body.appendChild(errorBox)
  panel.appendChild(body)

  // ── Footer ────────────────────────────────────────────────────────────
  //
  //   FIRST PROMPT  → Decline / Allow. This is a consent REQUEST; the two
  //     answers are the decision, and there is nothing yet to edit.
  //   MANAGING      → Cancel / Save. Every row is now editable (a switch and a
  //     forget), so the dialog holds a draft and the footer commits it. A
  //     destructive verb like "Withdraw" would name only one of the things
  //     Save can now do.
  //   NOTHING AT ALL → Close. The dialog still OPENS and says so; "the button
  //     did nothing" is never an acceptable answer.
  const foot = el(doc, 'div', 'pc-foot')
  const declineBtn = el(doc, 'button', 'pc-btn pc-btn-decline', 'Decline') as HTMLButtonElement
  const allowBtn = el(doc, 'button', 'pc-btn pc-btn-allow', 'Allow') as HTMLButtonElement
  const cancelBtn = el(doc, 'button', 'pc-btn pc-btn-decline', 'Cancel') as HTMLButtonElement
  const saveBtn = el(doc, 'button', 'pc-btn pc-btn-allow', 'Save') as HTMLButtonElement
  const closeBtn = el(doc, 'button', 'pc-btn pc-btn-allow', 'Close') as HTMLButtonElement
  // The APP-level exit. A required field is locked on — correctly, it is not a
  // choice while you use the app — so without this the grant would be
  // unrevocable from here, which is what `DELETE /consent`'s own docblock cites
  // Article 7(3) about. Per-field switches answer "what may it see"; this
  // answers "may it see anything", and they are different questions.
  // Set apart from Cancel / Save (margin-right:auto) because it is neither a
  // commit nor a dismissal of the draft.
  const revokeBtn = el(doc, 'button', 'pc-btn pc-btn-revoke', 'Decline all') as HTMLButtonElement
  if (nothingAtAll) {
    foot.appendChild(closeBtn)
  } else if (manage) {
    foot.appendChild(revokeBtn)
    foot.appendChild(cancelBtn)
    foot.appendChild(saveBtn)
  } else {
    foot.appendChild(declineBtn)
    foot.appendChild(allowBtn)
  }
  panel.appendChild(foot)

  const buttons = [declineBtn, allowBtn, cancelBtn, saveBtn, closeBtn, revokeBtn]

  /** Removed here rather than only on the resolve paths, because `cleanup` is
   *  also what the React wrapper calls on unmount — a listener left on the
   *  document would outlive the dialog and swallow the next Escape. */
  let onKeyDown: ((e: KeyboardEvent) => void) | null = null
  function cleanup(): void {
    // `true` — the CAPTURE flag must match the one used to add it, or the
    // listener is never removed and outlives the dialog.
    if (onKeyDown) {
      doc.removeEventListener('keydown', onKeyDown, true)
      onKeyDown = null
      // Leave the stack, so whatever is beneath becomes the top layer again.
      const i = openDialogs.lastIndexOf(dialogToken)
      if (i !== -1) openDialogs.splice(i, 1)
    }
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay)
  }

  function resolveWith(decision: ConsentDecision | null): void {
    if (settled) return
    settled = true
    cleanup()
    options.onResolve(decision)
  }

  async function submit(decision: ConsentChoice, granted: string[], declined: string[]): Promise<void> {
    if (submitting) return
    submitting = true
    errorBox.style.display = 'none'
    buttons.forEach((b) => { b.disabled = true })
    const result = await recordConsent(
      { appFolderId: request.appFolderId, decision, granted, declined },
      {
        fetchImpl: options.fetchImpl,
        baseUrl: options.baseUrl,
        headers: options.headers,
        endpoint: options.endpoint,
        extraBody: options.extraBody,
        timeoutMs: options.timeoutMs,
      },
    )
    if (!result.ok) {
      errorBox.textContent = result.error ?? 'consent recording failed'
      errorBox.style.display = ''
      submitting = false
      buttons.forEach((b) => { b.disabled = false })
      return
    }
    resolveWith({ decision, grantedFields: result.grantedFields ?? granted })
  }

  /** The residue, carried through UNCHANGED by every write that is not about it.
   *
   *  A prompt does not render these rows (see `addGroup` above) — the app has
   *  stopped asking, so they are not part of the question. But the write
   *  REPLACES both columns, so leaving them out of the arrays does not leave
   *  them alone: it DELETES them. That is the bug `revokeBtn` was taught about
   *  and Decline / Allow were not, and it runs in the privacy-losing direction
   *  — a field the user had DECLINED came back undecided, so the next time the
   *  author declared it the prompt seeded it ON (`newFields` may default on)
   *  and Allow granted it. A refusal reversed by a control nobody touched.
   *
   *  Reachable on any DELTA prompt: the server throws `ConsentRequiredError`
   *  before its own residue prune, so a blocked app arrives here with the
   *  residue still on the row.
   *
   *  Split on `grantedIn` rather than `on`, because these rows have no switch
   *  on a prompt — `on` merely mirrors `grantedIn` for them — and a field in
   *  both columns of a corrupt row resolves the same way the seeding does. */
  const residueGranted = recordOnly.filter((f) => grantedIn.has(f))
  const residueDeclined = recordOnly.filter((f) => !grantedIn.has(f))

  // Decline = grant nothing THE APP IS ASKING FOR. The required fields land in
  // `declined`, so the server's getConsentGrantedFields throws
  // ConsentDeclinedError on the next scripts.run → 403 → the app won't run for
  // this user. The residue rides along untouched — Decline answers the
  // question on screen, and "withdraw from this app entirely" is Decline all.
  declineBtn.addEventListener('click', () =>
    void submit('decline', [...residueGranted], [...request.required, ...request.optional, ...residueDeclined]))
  // Allow = required (always) + whichever optional toggles are on, + the
  // residue unchanged. The `decision` label is informational only — the server
  // records the explicit granted/declined field sets and intersects them with
  // the manifest (widened to fields already on record, which is what lets the
  // residue survive the clamp).
  allowBtn.addEventListener('click', () => {
    const grantedOpt = request.optional.filter((f) => on.has(f))
    const declinedOpt = request.optional.filter((f) => !on.has(f))
    const decision: ConsentChoice = grantedOpt.length > 0 ? 'allow_all' : 'required_only'
    void submit(
      decision,
      [...request.required, ...grantedOpt, ...residueGranted],
      [...declinedOpt, ...residueDeclined],
    )
  })

  // SAVE — the whole record, as edited, through the ORDINARY write path.
  //
  // The write REPLACES the row, so sending exactly what should remain is what
  // makes Forget expressible: a field staged as `gone` is simply absent from
  // both arrays and disappears from the record — no second verb, no per-field
  // DELETE. (`DELETE /consent` exists and is the natural word for it, but it
  // is mounted only under `/sdk/*` while a share-view recipient writes
  // first-party through a POST-only route. One write path is what B needs.)
  //
  // `decision` is a label the server records alongside the explicit sets; the
  // sets are what govern. 'decline' when nothing survives switched on, so the
  // recorded shape matches what a Decline would have written.
  saveBtn.addEventListener('click', () => {
    const live = [...request.required, ...request.optional, ...recordOnly].filter((f) => !gone.has(f))
    const granted = live.filter((f) => on.has(f))
    // An untouched undecided field is written to NEITHER array — see `undecided`.
    const declined = live.filter((f) => !on.has(f) && !(undecided.has(f) && !touched.has(f)))
    const decision: ConsentChoice = granted.length === 0
      ? 'decline'
      : granted.some((f) => !request.required.includes(f)) ? 'allow_all' : 'required_only'
    void submit(decision, granted, declined)
  })
  // Decline all = refuse everything the app currently declares, and drop the
  // residue with it. Declining a REQUIRED field is what blocks the app, which
  // is the point: this is the "I no longer consent to this app" action, and it
  // is the same end state Decline reaches at the first prompt.
  // Refuse EVERYTHING — including the residue. The write REPLACES both columns,
  // so omitting `recordOnly` did not leave those decisions alone, it deleted
  // them: a field the user had DECLINED came back undecided, and the next time
  // the author re-declared it the prompt defaulted it ON and Allow granted it.
  // The most protective-sounding button produced the least protective record,
  // and the note above it teaches the opposite ("your decision stays on record
  // until you change it"). Staged edits are ignored deliberately — this is the
  // "withdraw from this app" lever, not a commit of the draft.
  revokeBtn.addEventListener('click', () =>
    void submit('decline', [], [...request.required, ...request.optional, ...recordOnly]))
  cancelBtn.addEventListener('click', () => resolveWith(null))
  closeBtn.addEventListener('click', () => resolveWith(null))

  if (dismissible) {
    overlay.addEventListener('click', (e) => {
      // `submitting` guarded, like the Escape handler below. Clicking the
      // backdrop mid-POST resolved null while the write went on to succeed, so
      // the host took the refusal branch and showed "you declined" over a grant
      // that had just been recorded.
      if (e.target === overlay && !submitting) resolveWith(null)
    })
    // Escape — promised by `MountConsentOptions.onResolve` and by
    // `dismissible`'s own docblock ("backdrop/Escape"), and never implemented:
    // there was no key handling in this file at all. It matters more now that
    // the dialog holds a DRAFT — a modal you cannot dismiss from the keyboard
    // traps anyone not using a mouse.
    //
    // Dismiss, never a decision: it resolves null and writes nothing, exactly
    // like the backdrop. Ignored mid-submit, where the buttons are already
    // disabled and abandoning would leave the caller waiting on a promise the
    // write is about to settle.
    openDialogs.push(dialogToken)
    onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || settled) return
      // Only the TOP dialog answers — see `openDialogs`. Returning without
      // stopping is what lets the one above us receive it.
      if (openDialogs[openDialogs.length - 1] !== dialogToken) return
      // CAPTURE + stopImmediatePropagation, because a bubble-phase listener
      // could not win. The share view's own `SharedFileOverlay` binds Escape on
      // `document` too, and it mounts FIRST (the dialog is rendered as its
      // child), so on one target listeners run in registration order and
      // `stopPropagation` from a later one changes nothing: Escape dismissed
      // this dialog AND closed the whole shared file underneath it, from a
      // keypress the user meant for the modal alone.
      //
      // Capturing at the document runs before every bubble listener anywhere,
      // and stopping there keeps the event from reaching them at all. Swallowing
      // it outright is correct while a modal is open — Escape belongs to the
      // top-most layer — and this runs only when `dismissible`, so the ppn.gg
      // popup (where closing the window is the dismiss) binds nothing.
      // SWALLOWED FIRST, dismissed second — and swallowed even mid-submit.
      // The guard used to return before this, so Escape during an in-flight
      // POST reached the share view's handler and closed the whole shared file
      // out from under a write that was about to succeed. A modal owns Escape
      // for as long as it is up; being busy is not a reason to hand the key to
      // the layer below, it is a reason not to act on it here.
      e.stopImmediatePropagation()
      if (submitting) return
      resolveWith(null)
    }
    doc.addEventListener('keydown', onKeyDown, true)
  }

  container.appendChild(overlay)
  return cleanup
}
